import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import ProgressBar from "aldoo-ra/ProgressBar"
import { showNotification } from "aldoo-ra/Notification"
import { AddSpamRule, UpdateSpamRule } from "./api"
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"

// Target options for the rule
const TARGET_OPTIONS = [
  { value: "email", label: "Email Address" },
  { value: "subject", label: "Email Subject" },
  { value: "content", label: "Email Content" },
  { value: "all", label: "All Components" },
]

export default function AddRuleDialog({
  isOpen,
  onClose,
  onRuleAdded,
  rule = null,
}) {
  const [pattern, setPattern] = useState("")
  const [description, setDescription] = useState("")
  const [selectedTargets, setSelectedTargets] = useState([])
  const [active, setActive] = useState(true)
  const [score, setScore] = useState(0.5)
  const [testText, setTestText] = useState("")
  const [testResult, setTestResult] = useState(null)
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [isSystemModule, setIsSystemModule] = useState(false)

  // Initialize form when editing an existing rule
  useEffect(() => {
    if (rule) {
      setPattern(rule.pattern || "")
      setDescription(rule.description || "")
      setActive(rule.active !== false)
      setScore(rule.score !== undefined ? rule.score : 0.5)
      setIsSystemModule(rule.pattern === "System Module")

      // Format targets for the dropdown
      const targetObjects = (rule.targets || []).map((target) => {
        return (
          TARGET_OPTIONS.find((option) => option.value === target) || {
            value: target,
            label: target,
          }
        )
      })

      setSelectedTargets(targetObjects)
    } else {
      // Reset form for a new rule
      setPattern("")
      setDescription("")
      setSelectedTargets([])
      setActive(true)
      setScore(0.5)
      setIsSystemModule(false)
    }

    // Reset test values
    setTestText("")
    setTestResult(null)
    setError("")
  }, [rule, isOpen])

  // Get the color based on score value
  const getScoreColor = (scoreValue) => {
    if (scoreValue >= 0.7) return "text-admin_error"
    if (scoreValue >= 0.4) return "text-admin_warning"
    return "text-admin_success"
  }

  // Get the label based on score value
  const getScoreLabel = (scoreValue) => {
    if (scoreValue >= 0.9) return "Very High"
    if (scoreValue >= 0.7) return "High"
    if (scoreValue >= 0.5) return "Medium"
    if (scoreValue >= 0.3) return "Low"
    return "Very Low"
  }

  // Validate the regex pattern
  const validatePattern = (pattern) => {
    if (!pattern) return false

    // System Module pattern is always valid
    if (pattern === "System Module") return true

    try {
      new RegExp(pattern)
      return true
    } catch (e) {
      return false
    }
  }

  // Test the pattern against some text
  const handleTestPattern = () => {
    if (!testText) {
      setTestResult({
        matched: false,
        message: "Please enter text to test against the pattern",
      })
      return
    }

    if (!validatePattern(pattern)) {
      setTestResult({
        matched: false,
        message: "Invalid pattern. Please fix the pattern first.",
      })
      return
    }

    try {
      const regex = new RegExp(pattern, "i") // Case insensitive
      const matched = regex.test(testText)

      setTestResult({
        matched,
        message: matched
          ? "Pattern matched the test text!"
          : "Pattern did not match the test text",
      })
    } catch (e) {
      setTestResult({
        matched: false,
        message: `Error testing pattern: ${e.message}`,
      })
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    // System modules have special validation
    if (!isSystemModule) {
      // Validate required fields
      if (!pattern) {
        setError("Pattern is required")
        return
      }

      if (!validatePattern(pattern)) {
        setError("Invalid regular expression pattern")
        return
      }
    }

    if (selectedTargets.length === 0) {
      setError("Please select at least one target")
      return
    }

    // Extract target values
    const targets = selectedTargets.map((t) => t.value)

    setError("")
    setSubmitting(true)

    try {
      let response

      if (rule) {
        // For system modules, don't update the pattern
        const updateData = isSystemModule
          ? {
              description,
              targets,
              active,
              score,
            }
          : {
              pattern,
              description,
              targets,
              active,
              score,
            }

        response = await UpdateSpamRule(rule._id, updateData)
      } else {
        // Create new rule
        response = await AddSpamRule({
          pattern,
          description,
          targets,
          score,
        })
      }

      if (response.success || response.result) {
        showNotification({
          message: rule
            ? "Rule updated successfully"
            : "Rule created successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })

        onRuleAdded && onRuleAdded()
        onClose()
      } else {
        setError(response.error || "Failed to save rule")
      }
    } catch (error) {
      setError("An unexpected error occurred")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={rule ? "Edit Spam Rule" : "Add Spam Rule"}
      variant="admin_compact"
    >
      <div className="p-6 space-y-6">
        {error && (
          <div className="p-3 mb-4 bg-admin_error/10 border border-admin_error text-admin_error rounded-md flex items-start">
            <ExclamationCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {isSystemModule && (
          <div className="p-3 mb-4 bg-admin_info_bg border border-admin_primary text-admin_primary rounded-md flex items-start">
            <ShieldCheckIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>
              This is a system filter module. Some settings cannot be modified.
            </span>
          </div>
        )}

        <div className="space-y-4">
          {/* Only show pattern field for non-system modules */}
          {!isSystemModule && (
            <Input
              variant="admin"
              label="Pattern (Regular Expression)"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="e.g. viagra|cialis|buy.now"
              required
              error={
                pattern && !validatePattern(pattern)
                  ? "Invalid regular expression"
                  : ""
              }
              styleOverrides={{
                label: "text-base font-bold text-admin_text mb-2",
              }}
            />
          )}

          {/* Only show regex examples for non-system modules */}
          {!isSystemModule && (
            <div className="flex items-start pt-1 pb-3 text-admin_text_secondary text-sm">
              <div>
                <div className="flex items-center gap-2">
                  <InformationCircleIcon className="w-5 h-5 flex-shrink-0 text-admin_primary" />

                  <Typography variant="admin_body" className="my-0">
                    Regular expressions are case-insensitive. Examples:
                  </Typography>
                </div>

                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <code>viagra|cialis</code> - Match emails containing either
                    word
                  </li>
                  <li>
                    <code>free.money</code> - Match "free money" (dot matches
                    any character)
                  </li>
                  <li>
                    <code>\b(winner|prize)\b</code> - Match whole words "winner"
                    or "prize"
                  </li>
                  <li>
                    <code>
                      \d{4}-\d{4}-\d{4}
                    </code>{" "}
                    - Match patterns like credit card numbers
                  </li>
                </ul>
              </div>
            </div>
          )}

          <Input
            variant="admin"
            label="Description"
            value={description}
            onChange={(e) => !isSystemModule && setDescription(e.target.value)}
            placeholder="Enter a description for this rule"
            readOnly={isSystemModule}
            styleOverrides={{
              label: "text-base font-bold text-admin_text mb-2",
              inputClass: isSystemModule ? "bg-admin_neutral_1/30" : "",
            }}
          />

          <div>
            <Typography
              variant="admin_body_accent"
              className="text-base font-bold mb-2"
            >
              Targets
              <span className="text-admin_error ml-1">*</span>
            </Typography>
            
            <DropDownSelect
              variant="admin"
              options={TARGET_OPTIONS}
              value={selectedTargets}
              onChange={(values) => setSelectedTargets(values || [])}
              className="w-full"
              multiSelection
              placeholder="Select where to apply this rule"
            />
            {selectedTargets.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedTargets.map((target, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-admin_info_bg text-admin_text_secondary rounded-full text-xs"
                  >
                    {target.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Score Field */}
          <div className="mt-4">
            <Typography
              variant="admin_body_accent"
              className="text-base font-bold mb-2"
            >
              Confidence Score
              <span className="text-admin_error ml-1">*</span>
            </Typography>

            <div className="flex items-center mb-3">
              <span
                className={`font-medium ${getScoreColor(score)} mr-2 text-lg`}
              >
                {score.toFixed(1)}
              </span>
              <span className="text-admin_text_secondary">
                ({getScoreLabel(score)})
              </span>

              <div className="flex-grow"></div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="admin_secondary"
                  className="py-1 px-2 min-w-[40px]"
                  onClick={() => setScore(Math.max(0, score - 0.1))}
                  disabled={score <= 0}
                >
                  -
                </Button>

                <Button
                  variant="admin_secondary"
                  className="py-1 px-2 min-w-[40px]"
                  onClick={() => setScore(Math.min(1, score + 0.1))}
                  disabled={score >= 1}
                >
                  +
                </Button>
              </div>
            </div>

            <ProgressBar
              progress={score}
              title=""
              styleOverrides={{
                progressFill: `${getScoreColor(score).replace("text-", "bg-")}`,
                progressContainer: "h-2 rounded-full bg-admin_neutral_1",
              }}
            />

            <div className="flex justify-between text-xs text-admin_text_secondary mt-1">
              <span>0.0 (Weak indicator)</span>
              <span>1.0 (Strong indicator)</span>
            </div>

            <div className="text-sm text-admin_text_secondary mt-4">
              <p>Set how strongly this rule indicates spam when matched:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>
                  <span className="text-admin_success">Low (0.1-0.3)</span>:
                  Minor indicator, like promotional keywords
                </li>
                <li>
                  <span className="text-admin_warning">Medium (0.4-0.6)</span>:
                  Moderate indicator, like suspicious patterns
                </li>
                <li>
                  <span className="text-admin_error">High (0.7-1.0)</span>:
                  Strong indicator, like known spam phrases
                </li>
              </ul>
            </div>
          </div>

          {/* Test Pattern Section - Only shown for non-system modules */}
          {!isSystemModule && (
            <div className="mt-6 p-4 bg-admin_canvas rounded-md">
              <Typography
                variant="admin_body_accent"
                className="font-bold mt-0 mb-3"
              >
                Test Pattern
              </Typography>

              <div className="space-y-3">
                <Input
                  variant="admin"
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="Enter text to test against the pattern"
                  styleOverrides={{
                    inputClass: "bg-admin_paper",
                  }}
                />

                <Button
                  variant="admin_secondary"
                  onClick={handleTestPattern}
                  disabled={!pattern || !validatePattern(pattern)}
                  className="w-full"
                >
                  Test Pattern
                </Button>

                {testResult && (
                  <div
                    className={`mt-2 p-3 rounded-md ${
                      testResult.matched
                        ? "bg-admin_success/10 text-admin_success"
                        : "bg-admin_error/10 text-admin_error"
                    }`}
                  >
                    {testResult.message}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center gap-4 mt-8">
          {rule && (
            <div>
              <Checkbox
                variant="admin"
                label="Rule is active"
                isOn={active}
                onChange={() => setActive(!active)}
              />
            </div>
          )}

          {/* If not editing a rule, add an empty div for spacing */}
          {!rule && <div></div>}

          <div className="flex gap-4">
            <Button variant="admin_secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                (!isSystemModule && !validatePattern(pattern)) ||
                selectedTargets.length === 0
              }
            >
              {submitting ? "Saving..." : rule ? "Update Rule" : "Add Rule"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
